<script setup lang="ts">
const props = withDefaults(defineProps<{
  color?: string
  variant?: 'flat' | 'outlined' | 'text' | 'tonal' | 'elevated'
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large'
  icon?: boolean | string
  block?: boolean
  rounded?: boolean | string
  disabled?: boolean
  loading?: boolean
  href?: string
  to?: string | object
  type?: string
  density?: 'default' | 'comfortable' | 'compact'
  prependIcon?: string
  appendIcon?: string
  value?: any
}>(), {
  variant: 'elevated',
  size: 'default',
  type: 'button',
  density: 'default',
})

const emit = defineEmits<{
  click: [e: MouseEvent]
}>()

const toggleValue = inject<Ref<any> | null>('m-btn-toggle-value', null)
const toggleSelect = inject<((val: any) => void) | null>('m-btn-toggle-select', null)

const isToggleActive = computed(() => {
  if (!toggleValue?.value || props.value === undefined) return false
  if (Array.isArray(toggleValue.value)) return toggleValue.value.includes(props.value)
  return toggleValue.value === props.value
})

const NuxtLink = defineNuxtLink({})

const tag = computed(() => {
  if (props.to) return NuxtLink
  if (props.href) return 'a'
  return 'button'
})

const bindAttrs = computed(() => {
  if (props.to) return { to: props.to }
  if (props.href) return { href: props.href, target: '_blank', rel: 'noopener' }
  return { type: props.type }
})

function handleClick(e: MouseEvent) {
  if (toggleSelect && props.value !== undefined) {
    toggleSelect(props.value)
  }
  emit('click', e)
}
</script>

<template>
  <component
    :is="tag"
    v-bind="bindAttrs"
    class="m-btn"
    :class="[
      `m-btn--${variant}`,
      `m-btn--${size}`,
      `m-btn--${density}`,
      {
        'm-btn--icon': icon,
        'm-btn--block': block,
        'm-btn--disabled': disabled,
        'm-btn--loading': loading,
        'm-btn--active': isToggleActive,
        [`m-btn--color-${color}`]: color,
      }
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="m-btn__loader">
      <svg class="m-btn__spinner" viewBox="0 0 24 24" :width="20" :height="20">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="31.4 31.4" />
      </svg>
    </span>
    <MIcon v-if="prependIcon" :icon="prependIcon" size="small" class="m-btn__prepend" />
    <span v-if="!icon" class="m-btn__content">
      <slot />
    </span>
    <MIcon v-if="typeof icon === 'string'" :icon="icon" :size="size === 'x-small' ? 'x-small' : size === 'small' ? 'small' : 'default'" />
    <slot v-else-if="icon" />
    <MIcon v-if="appendIcon" :icon="appendIcon" size="small" class="m-btn__append" />
  </component>
</template>

<style scoped>
.m-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--m-space-2);
  border: none;
  border-radius: var(--m-radius);
  cursor: pointer;
  font-family: var(--m-font-body);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--m-transition);
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  user-select: none;
  line-height: 1.5;
  letter-spacing: 0.02em;
}

/* Sizes */
.m-btn--x-small { padding: 2px 8px; font-size: var(--m-font-size-xs); min-height: 26px; }
.m-btn--small { padding: 4px 12px; font-size: var(--m-font-size-sm); min-height: 32px; }
.m-btn--default { padding: 6px 16px; font-size: var(--m-font-size-sm); min-height: 40px; }
.m-btn--large { padding: 8px 24px; font-size: var(--m-font-size-base); min-height: 48px; }
.m-btn--x-large { padding: 10px 32px; font-size: var(--m-font-size-lg); min-height: 56px; }

/* Density */
.m-btn--compact { min-height: auto; padding-block: 2px; }
.m-btn--comfortable { min-height: auto; padding-block: 4px; }

/* Icon button */
.m-btn--icon {
  padding: 0;
  border-radius: var(--m-radius-full);
}
.m-btn--icon.m-btn--x-small { width: 26px; height: 26px; }
.m-btn--icon.m-btn--small { width: 32px; height: 32px; }
.m-btn--icon.m-btn--default { width: 40px; height: 40px; }
.m-btn--icon.m-btn--large { width: 48px; height: 48px; }

/* Variants — default (no color) */
.m-btn--elevated {
  background: var(--m-surface);
  color: var(--m-on-surface);
  box-shadow: var(--m-shadow);
}
.m-btn--elevated:hover { box-shadow: var(--m-shadow-md); }

.m-btn--flat {
  background: var(--m-surface-variant);
  color: var(--m-on-surface);
  box-shadow: none;
}
.m-btn--flat:hover { filter: brightness(0.95); }

.m-btn--outlined {
  background: transparent;
  color: var(--m-on-surface);
  border: 1px solid var(--m-border);
}
.m-btn--outlined:hover { background: rgba(0,0,0,0.04); }

.m-btn--text {
  background: transparent;
  color: var(--m-on-surface);
}
.m-btn--text:hover { background: rgba(0,0,0,0.04); }

.m-btn--tonal {
  background: var(--m-surface-variant);
  color: var(--m-on-surface-variant);
}
.m-btn--tonal:hover { filter: brightness(0.95); }

/* Color variants */
.m-btn--color-primary.m-btn--elevated,
.m-btn--color-primary.m-btn--flat { background: var(--m-primary); color: var(--m-on-primary); }
.m-btn--color-primary.m-btn--outlined { color: var(--m-primary); border-color: var(--m-primary); }
.m-btn--color-primary.m-btn--text { color: var(--m-primary); }
.m-btn--color-primary.m-btn--tonal { background: color-mix(in srgb, var(--m-primary) 15%, transparent); color: var(--m-primary); }

.m-btn--color-secondary.m-btn--elevated,
.m-btn--color-secondary.m-btn--flat { background: var(--m-secondary); color: var(--m-on-secondary); }
.m-btn--color-secondary.m-btn--outlined { color: var(--m-secondary); border-color: var(--m-secondary); }
.m-btn--color-secondary.m-btn--text { color: var(--m-secondary); }
.m-btn--color-secondary.m-btn--tonal { background: color-mix(in srgb, var(--m-secondary) 15%, transparent); color: var(--m-secondary); }

.m-btn--color-error.m-btn--elevated,
.m-btn--color-error.m-btn--flat { background: var(--m-error); color: #fff; }
.m-btn--color-error.m-btn--outlined { color: var(--m-error); border-color: var(--m-error); }
.m-btn--color-error.m-btn--text { color: var(--m-error); }
.m-btn--color-error.m-btn--tonal { background: color-mix(in srgb, var(--m-error) 15%, transparent); color: var(--m-error); }

.m-btn--color-success.m-btn--elevated,
.m-btn--color-success.m-btn--flat { background: var(--m-success); color: #fff; }
.m-btn--color-success.m-btn--outlined { color: var(--m-success); border-color: var(--m-success); }
.m-btn--color-success.m-btn--text { color: var(--m-success); }

.m-btn--color-warning.m-btn--elevated,
.m-btn--color-warning.m-btn--flat { background: var(--m-warning); color: #333; }
.m-btn--color-warning.m-btn--outlined { color: var(--m-warning); border-color: var(--m-warning); }

.m-btn--color-white.m-btn--text,
.m-btn--color-white.m-btn--outlined { color: #fff; }

/* Block */
.m-btn--block { display: flex; width: 100%; }

/* States */
.m-btn--disabled { opacity: 0.5; pointer-events: none; }
.m-btn--loading .m-btn__content { opacity: 0; }

.m-btn__loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.m-btn__spinner {
  animation: m-spin 0.8s linear infinite;
}

@keyframes m-spin {
  to { transform: rotate(360deg); }
}

.m-btn--active {
  background: var(--m-primary);
  color: var(--m-on-primary);
  border-color: var(--m-primary);
}

.m-btn__prepend { margin-right: var(--m-space-1); }
.m-btn__append { margin-left: var(--m-space-1); }
</style>
